import { useEffect, useState } from "react";
import {
  Button,
  Code,
  Container,
  Group,
  Modal,
  NumberFormatter,
  NumberInput,
  Select,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { Link, useLocation } from "wouter";
import { IconPlus } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { getAdminPartners, modifyCredits, updateAdminPartnerSkuOffer } from "../api";
import { skusToCredits } from "../utils/skus";
import ListPending from "../_Shared/ListPending";

export default function PartnerList() {
  const [partners, setPartners] = useState(null);
  const [addTo, setAddTo] = useState(null);
  const [offerTo, setOfferTo] = useState(null);
  const [offerCurrency, setOfferCurrency] = useState("gbp");
  const [offerMinor, setOfferMinor] = useState(50);
  const [skuCount, setSkuCount] = useState(1000);
  const [saving, setSaving] = useState(false);
  const [, setLocation] = useLocation();

  const refresh = () =>
    getAdminPartners()
      .then((data) => setPartners(Array.isArray(data) ? data : []))
      .catch(() => setPartners([]));

  useEffect(() => {
    refresh();
  }, []);

  return (
    <Container size="xl">
      <Group justify="space-between" align="center">
        <div>
          <Title order={1}>Partners</Title>
          <Text c="dimmed" size="sm" mt={4}>
            Marketplace partner tenants. They get the partner portal, not the
            merchant app.
          </Text>
        </div>
        <Button
          leftSection={<IconPlus size={16} />}
          variant="default"
          component={Link}
          to="/admin/partners/create"
        >
          Create partner
        </Button>
      </Group>

      <ListPending
        items={partners}
        emptyText="No partners yet. Create one to provision a partner portal tenant."
      >
        {(rows) => (
      <Table mt="lg" highlightOnHover striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Email</Table.Th>
            <Table.Th>Marketplace</Table.Th>
            <Table.Th>Offer</Table.Th>
            <Table.Th ta="right">SKU pool</Table.Th>
            <Table.Th ta="right">Merchants</Table.Th>
            <Table.Th>Tenant</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((partner) => (
            <Table.Tr
              key={partner.tenantId}
              style={{ cursor: "pointer" }}
              onClick={() => setLocation(`/tenants/${partner.tenantId}`)}
            >
              <Table.Td>{partner.displayName || partner.tenantName}</Table.Td>
              <Table.Td>{partner.email || "—"}</Table.Td>
              <Table.Td>{partner.marketplaceName || partner.marketplaceId}</Table.Td>
              <Table.Td>
                {partner.offerCurrency
                  ? `${partner.offerCurrency} ${partner.offerMinorPerSku}`
                  : "—"}
              </Table.Td>
              <Table.Td ta="right">
                <NumberFormatter
                  value={partner.skuPoolRemaining}
                  thousandSeparator
                />
              </Table.Td>
              <Table.Td ta="right">
                <NumberFormatter
                  value={partner.merchantCount}
                  thousandSeparator
                />
              </Table.Td>
              <Table.Td>
                <Code fz="sm">{partner.tenantId}</Code>
              </Table.Td>
              <Table.Td>
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={(event) => {
                    event.stopPropagation();
                    setOfferCurrency(partner.offerCurrency || "gbp");
                    setOfferMinor(partner.offerMinorPerSku || 50);
                    setOfferTo(partner);
                  }}
                >
                  Offer
                </Button>
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={(event) => {
                    event.stopPropagation();
                    setSkuCount(1000);
                    setAddTo(partner);
                  }}
                >
                  Add SKUs
                </Button>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
        )}
      </ListPending>

      <Modal
        opened={!!addTo}
        onClose={() => setAddTo(null)}
        title={`Add SKUs to ${addTo?.displayName || addTo?.tenantName || "pool"}`}
      >
        <Text size="sm" c="dimmed" mb="sm">
          Grows the {addTo?.marketplaceName || "marketplace"} trial pool.
          Merchants spend these only on that marketplace.
        </Text>
        <NumberInput
          label="SKUs"
          min={1}
          value={skuCount}
          onChange={setSkuCount}
        />
        <Button
          mt="md"
          loading={saving}
          disabled={!(Number(skuCount) >= 1)}
          onClick={async () => {
            setSaving(true);
            try {
              await modifyCredits(addTo.tenantId, {
                credits: skusToCredits(skuCount),
                note: "Partner pool top-up",
                creditType: addTo.creditType,
                validDays: 365,
              });
              notifications.show({
                title: "SKU pool updated",
                color: "green",
              });
              setAddTo(null);
              refresh();
            } finally {
              setSaving(false);
            }
          }}
        >
          Add to pool
        </Button>
      </Modal>

      <Modal
        opened={!!offerTo}
        onClose={() => setOfferTo(null)}
        title={`SKU offer for ${offerTo?.displayName || offerTo?.tenantName || "partner"}`}
      >
        <Text size="sm" c="dimmed" mb="sm">
          Template for merchants this partner creates next. Existing merchants
          keep the copy they already have.
        </Text>
        <Select
          label="Currency"
          data={[
            { value: "gbp", label: "GBP" },
            { value: "eur", label: "EUR" },
            { value: "usd", label: "USD" },
          ]}
          value={offerCurrency}
          onChange={(val) => setOfferCurrency(val || "gbp")}
        />
        <NumberInput
          mt="sm"
          label="Minor units per SKU"
          min={1}
          value={offerMinor}
          onChange={setOfferMinor}
        />
        <Button
          mt="md"
          loading={saving}
          disabled={!offerCurrency || !(Number(offerMinor) >= 1)}
          onClick={async () => {
            setSaving(true);
            try {
              await updateAdminPartnerSkuOffer(offerTo.tenantId, {
                currency: offerCurrency,
                minorPerSku: Number(offerMinor),
              });
              notifications.show({
                title: "Partner offer updated",
                color: "green",
              });
              setOfferTo(null);
              refresh();
            } finally {
              setSaving(false);
            }
          }}
        >
          Save template
        </Button>
      </Modal>
    </Container>
  );
}
